import React, { useRef, useEffect, useContext } from 'react';
import styled from '@emotion/styled';
import Avatar from '@mui/material/Avatar';
import ChatBotIcon from './ChatBotIcon';
import { keyframes } from '@emotion/react';
import InfoIcon from '@mui/icons-material/Info';
import { SupportContext } from './SupportContext';
import { Button, Grid, Typography } from '@platform-ui/design-system';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import ChatBotUnavailable from './ChatBotUnavailable';
import { NAVIGATION_ENDPOINTS } from '../nav/constants/urls';
import ChatBotFeedback from './ChatBotFeedback';
const LoadingAnimation = keyframes`
 to {
    clip-path: inset(0 -1ch 0 0);
  }
`;
const QUOTA_OVER_ERROR_CODE = 429;
const TIMED_OUT_ERROR_CODE = 408;
const CHAT_BOT_UNAVAILABLE = [500, 503, 504];

const Loading = styled.div`
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  clip-path: inset(0 1ch 0 0);
  animation-name: ${LoadingAnimation};
  animation-duration: 1s;
  animation-timing-function: steps(3);
  animation-iteration-count: infinite;
  animation-delay: 0s;
`;

const TextContainer = styled.div`
  margin-bottom: 24px;
  & > span {
    color: rgba(0, 0, 0, 0.6);
    font-size: 14px;
  }
`;

const ChatElementContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const ChatElementNoResponse = styled.span`
  display: flex;
  align-items: center;
  background: #ffe9e5;
  padding: 10px;
  border-radius: 4px;
`;

const ChatElementNoResponseInnerDiv = styled.div`
  display: flex;
  width: 83%;
`;

const ConversationContainerDiv = styled.div`
  padding-top: 20;
  min-height: calc(100vh - 250px);
  flex-direction: column-reverse;
  display: flex;
`;

const ChatElementAlert = styled.span`
  font-weight: 400;
  color: rgba(0, 0, 0, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 8px 12px;
  border-radius: 4px;
  word-break: break-word;
`;

const ChatElementResponse = styled(ChatElementAlert)`
  margin-top: 10px;
`;

const ChatElementQuotaOver = styled.span`
  display: flex;
  align-items: center;
  background: #ffe9e5;
  padding: 10px;
  border-radius: 4px;
`;

const ChatElementQuotaOverInnerDiv = styled.div`
  display: flex;
  width: 83%;
`;

const ConversationContainer = styled.div`
  width: 100%;
  height: calc(100vh - 200px);
  overflow: auto;
  margin-bottom: 20px;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AlertMessage = styled.div`
  display: flex;
  background: rgba(228, 241, 255, 1);
  padding: 16px 20px;
  border-radius: 4px;
`;

const ChatElementInputDisplay = styled.span`
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  padding: 8px 12px;
`;

const TryAgain = styled.span`
  cursor: pointer;
  color: #0d4ac3;
`;

const ChatBotUnavailableContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 290px);
`;

const ChatBotUnavailableImg = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ChatBotUnavailableHeading = styled.div`
  font-weight: 600 !important;
  font-size: 17px;
  line-height: 28px;
  text-align: center;
  letter-spacing: 0.1px;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 14px;
  margin-bottom: 4px;
`;

const ChatBotUnavailableBody = styled.div`
  font-weight: 400 !important;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  letter-spacing: 0.1px;
  color: rgba(0, 0, 0, 0.6);
`;

const ChatExperience = ({ identityData }) => {
  const {
    currentConversation,
    conversationHistory,
    setConversationHistory,
    isBotAnswering,
    quotaOver,
    setQuotaOver,
    timeoutError,
    setTimeoutError,
    getResponse,
    setIsBotAnswering,
    chatBotUnavailable,
    setChatBotUnavailable,
    alertMessageCancel,
    setAlertMessageReadCancel,
  } = useContext(SupportContext);
  const { t } = useTranslation();
  const fetchCurrentConversationHistory = async () => {
    try {
      const response = await fetch(
        `${NAVIGATION_ENDPOINTS.GET_COPILOT_SUPPORT_CONVERSATIONS}/${currentConversation.id}`,
        {
          headers: {
            'zuora-tenant-id': identityData.tenantId,
            'zuora-user-id': identityData.coreUserId,
          },
        },
      );
      const { status } = response;
      if (status === QUOTA_OVER_ERROR_CODE) setQuotaOver(true);
      else if (status === TIMED_OUT_ERROR_CODE) setTimeoutError(true);
      else if (CHAT_BOT_UNAVAILABLE.includes(status)) {
        setChatBotUnavailable(true);
      } else {
        const { data } = await response.json();
        const check = '^Oops, error happened';
        const regex = new RegExp(check);
        if (regex.test(data)) setTimeoutError(false);
        else {
          setConversationHistory([...data.reverse()]);
          setQuotaOver(false);
          setTimeoutError(false);
          setChatBotUnavailable(false);
        }
      }
    } catch (e) {
      console.log(e);
    }
    setIsBotAnswering(false);
  };
  useEffect(() => {
    if (currentConversation) {
      fetchCurrentConversationHistory();
    }
  }, [currentConversation?.id]);

  const getHtml = (text) => {
    const containsHTML = (text) => /<[a-z][\s\S]*>/i.test(text);
    if (containsHTML(text)) {
      return text?.replace(/\n/g, '<br>');
    } else {
      let escapedText: any = new DOMParser().parseFromString(text, 'text/html')
        .body.textContent;
      escapedText = escapedText?.replace(
        /((http:|https:)[^\s]+[\w])/g,
        '<a href="$1" target="_blank">$1</a>',
      );
      return escapedText?.replace(/\n/g, '<br>');
    }
  };
  const messagesEndRef: any = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory]);
  const BasicAlertMessage = () => {
    return (
      <>
        {!alertMessageCancel && (
          <AlertMessage>
            <InfoIcon
              sx={{
                color: 'rgba(50, 105, 212, 1)',
                margin: ' 2px 9px 0px 0px',
              }}
            />
            <div>
              <TextContainer>
                <Typography variant="title">
                  {t('ChatBot.Disclaimer.Heading')}
                </Typography>
                <Typography variant="body2">
                  {t('ChatBot.Disclaimer.Text')}
                </Typography>
              </TextContainer>
              <Button
                variant="text"
                dsOnClick={() => {
                  setAlertMessageReadCancel(!alertMessageCancel);
                }}
                size="small"
              >
                {t('ChatBot.Disclaimer.Close')}
              </Button>
            </div>
          </AlertMessage>
        )}
      </>
    );
  };

  const ChatBotUnavailableModal = () => {
    return (
      <>
        <ChatBotUnavailableContainer>
          <Grid>
            <Grid item sm={12}>
              <ChatBotUnavailableImg>
                <ChatBotUnavailable />
              </ChatBotUnavailableImg>
            </Grid>
            <Grid item sm={12}>
              <ChatBotUnavailableHeading>
                <Typography variant="title">
                  {t('ChatBot.Error.BotUnavailableHeading')}
                </Typography>
              </ChatBotUnavailableHeading>
            </Grid>
            <Grid item sm={12}>
              <ChatBotUnavailableBody>
                <Typography variant="body2">
                  {t('ChatBot.Error.BotUnavailableBody')}
                </Typography>
              </ChatBotUnavailableBody>
            </Grid>
          </Grid>
        </ChatBotUnavailableContainer>
      </>
    );
  };
  const renderConversation = () => {
    return (
      <ChatContainer>
        {chatBotUnavailable && <ChatBotUnavailableModal />}
        {conversationHistory.length < 1 && <BasicAlertMessage />}
        {quotaOver && conversationHistory.length < 1 && (
          <ChatElementContainer data-e2e="quota-error-msg">
            <div>
              <ChatBotIcon
                style={{ marginRight: '10px', alignSelf: 'flex-start' }}
              />
            </div>
            <ChatElementQuotaOver>
              <ChatElementQuotaOverInnerDiv>
                <HighlightOffOutlinedIcon
                  sx={{ color: '#B60F00', marginRight: '8px' }}
                />
                <Typography e2e="quota-error-msg">
                  {t('ChatBot.Error.Quota')}
                </Typography>
              </ChatElementQuotaOverInnerDiv>
            </ChatElementQuotaOver>
          </ChatElementContainer>
        )}
        <ChatElementContainer>
          <div>
            <ChatBotIcon
              style={{
                margin: '10px',
                marginLeft: '0px',
                alignSelf: 'flex-start',
              }}
            />
          </div>
          <ChatElementResponse>{t('ChatBot.HiMessage')}</ChatElementResponse>
        </ChatElementContainer>
        {conversationHistory.length > 0 &&
          conversationHistory.map(({ role, content }) => {
            if (role === 'assistant') {
              const htmlContent = DOMPurify.sanitize(getHtml(content), {
                ADD_ATTR: ['target'],
              });
              return (
                <ChatElementContainer key={content} data-e2e="answer">
                  <div>
                    <ChatBotIcon
                      style={{ marginRight: '10px', alignSelf: 'flex-start' }}
                    />
                  </div>
                  <ChatElementAlert>
                    <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
                    <ChatBotFeedback />
                  </ChatElementAlert>
                </ChatElementContainer>
              );
            } else {
              return (
                <>
                  <ChatElementContainer key={content}>
                    <Avatar
                      sx={{
                        marginRight: '10px',
                        bgcolor: '#00D2B9',
                        height: 32,
                        width: 32,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {identityData?.username?.[0]?.toUpperCase()}
                    </Avatar>
                    <ChatElementInputDisplay>{content}</ChatElementInputDisplay>
                  </ChatElementContainer>
                </>
              );
            }
          })}
        {isBotAnswering && (
          <ChatElementContainer>
            <div>
              <ChatBotIcon
                style={{ marginRight: '10px', alignSelf: 'flex-start' }}
              />
            </div>
            <Loading>{t('ChatBot.Searching')}</Loading>
          </ChatElementContainer>
        )}
        {quotaOver && (
          <ChatElementContainer data-e2e="quota-error-msg">
            <div>
              <ChatBotIcon
                style={{ marginRight: '10px', alignSelf: 'flex-start' }}
              />
            </div>
            <ChatElementNoResponse>
              <ChatElementNoResponseInnerDiv>
                <HighlightOffOutlinedIcon
                  sx={{ color: '#B60F00', marginRight: '8px' }}
                />
                <Typography e2e="quota-error-msg">
                  {t('ChatBot.Error.Quota')}
                </Typography>
              </ChatElementNoResponseInnerDiv>
            </ChatElementNoResponse>
          </ChatElementContainer>
        )}
        {timeoutError && (
          <ChatElementContainer>
            <div>
              <ChatBotIcon
                style={{ marginRight: '10px', alignSelf: 'flex-start' }}
              />
            </div>
            <ChatElementNoResponse>
              <ChatElementNoResponseInnerDiv>
                <HighlightOffOutlinedIcon
                  sx={{ color: '#B60F00', marginRight: '8px' }}
                />
                <Typography sx={{ width: '83%' }} e2e="timeout-error-msg">
                  {t('ChatBot.Error.Timeout')}
                </Typography>
              </ChatElementNoResponseInnerDiv>
              <TryAgain onClick={() => getResponse(true)}>
                {t('ChatBot.Error.TryAgain')}{' '}
              </TryAgain>
            </ChatElementNoResponse>
          </ChatElementContainer>
        )}
        <div ref={messagesEndRef} />
      </ChatContainer>
    );
  };
  return (
    <>
      <ConversationContainerDiv>
        <ConversationContainer>{renderConversation()}</ConversationContainer>
      </ConversationContainerDiv>
    </>
  );
};
export default ChatExperience;
